import * as React from 'react'
import * as XLSX from 'xlsx'
import * as Papa from 'papaparse'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { API, ONComplianceReportRequest } from '@getgreenline/homi-shared'
import { FormError } from '../../../../../../components/FormError'
import { ComplianceStore } from '../../ComplianceStore'
import { numberPadder, parseErrorMsg } from '../../../../../../utilities/helpers'
import { Button } from 'antd'
import { Track } from '../../../../../../Track'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  complianceStore: ComplianceStore
  nameOfStore?: string
  retailStoreAuthorization: string
}

interface State {
  isLoading: boolean
  errorMessage?: string
  city?: string
  managementEmployeeCount: number
  adminEmployeeCount: number
  salesEmployeeCount: number
  productionEmployeeCount: number
  otherEmployeeCount: number
}

export class OntarioFederalReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      managementEmployeeCount: 0,
      adminEmployeeCount: 0,
      salesEmployeeCount: 0,
      productionEmployeeCount: 0,
      otherEmployeeCount: 0,
    }
  }

  generalInformationRows = () => {
    const {
      managementEmployeeCount,
      adminEmployeeCount,
      salesEmployeeCount,
      productionEmployeeCount,
      otherEmployeeCount,
    } = this.state
    return [
      ['Employee Category', 'Number'],
      ['Management', managementEmployeeCount],
      ['Administrative', adminEmployeeCount],
      ['Sales', salesEmployeeCount],
      ['Production', productionEmployeeCount],
      ['Other', otherEmployeeCount],
    ]
  }

  informationHeaderRows = () => {
    const { city } = this.state
    const { nameOfStore, retailStoreAuthorization } = this.props
    const { month, year } = this.props.complianceStore

    const formattedMonth = numberPadder(month, 2)
    const emptyCols = new Array(4)

    const nameReportingStoreRow = ['Name of Reporting Retail Store', ...emptyCols, nameOfStore]
    const retailStoreAuthorizationRow = [
      'Retail Store Authorization (RSA)',
      ...emptyCols,
      retailStoreAuthorization,
    ]
    const cityRow = ['City', ...emptyCols, city]
    const reportingPeriodRow = [
      'Reporting Period: (yyyy/mm)',
      ...emptyCols,
      `${year}/${formattedMonth}`,
    ]

    const emptyRows = new Array(5)

    return [
      ...emptyRows,
      nameReportingStoreRow,
      retailStoreAuthorizationRow,
      cityRow,
      reportingPeriodRow,
    ]
  }

  generateFederalReport = async () => {
    const { month, year, locationIds } = this.props.complianceStore

    const params: ONComplianceReportRequest = {
      month,
      year,
      locationIds,
      reportType: 'federal',
    }

    Track.track('dashboard:compliance_complianceReportGenerated', params)

    const company = this.props.currentCompanyStore.company!
    this.setState({
      isLoading: true,
      errorMessage: undefined,
    })

    try {
      const response = await API.getONComplianceReport<'csv', 'federal'>(company.id, 'csv', params)

      this.setState({ isLoading: false })
      const parsedData = Papa.parse(response)
      const informationHeaderRows = this.informationHeaderRows()
      const generalInformationRows = this.generalInformationRows()

      const dataRows = [...informationHeaderRows, [], ...parsedData.data]

      /* convert from array of arrays to workbook */
      const inventoryAndSales = XLSX.utils.aoa_to_sheet(dataRows)
      const generalInformation = XLSX.utils.aoa_to_sheet(generalInformationRows)

      const mergeNameLabel = XLSX.utils.decode_range('A6:E6')
      const mergeNameValue = XLSX.utils.decode_range('F6:V6')
      const mergeRSALabel = XLSX.utils.decode_range('A7:E7')
      const mergeRSAValue = XLSX.utils.decode_range('F7:V7')
      const mergeReportingCityLabel = XLSX.utils.decode_range('A8:E8')
      const mergeReportingCityValue = XLSX.utils.decode_range('F8:V8')
      const mergeReportingPeriodLabel = XLSX.utils.decode_range('A9:E9')
      const mergeReportingPeriodValue = XLSX.utils.decode_range('F9:V9')
      inventoryAndSales['!merges'] = [
        mergeNameLabel,
        mergeNameValue,
        mergeRSALabel,
        mergeRSAValue,
        mergeReportingCityLabel,
        mergeReportingCityValue,
        mergeReportingPeriodLabel,
        mergeReportingPeriodValue,
      ]

      const new_workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(new_workbook, inventoryAndSales, 'Inventory and Sales')
      XLSX.utils.book_append_sheet(new_workbook, generalInformation, 'General Information')

      const fileMonth = numberPadder(month, 2)
      const fileYear = year.toString().slice(2)

      const title = `${company.name}_RSA${this.props.retailStoreAuthorization}_FED_${fileMonth}${fileYear}.xlsx`
      XLSX.writeFile(new_workbook, title)
    } catch (error) {
      this.setState({
        isLoading: false,
        errorMessage: parseErrorMsg(error),
      })
    }
  }

  render() {
    const { isLoading, errorMessage } = this.state
    const { locationIds } = this.props.complianceStore

    return (
      <div>
        <h5>Federal report</h5>

        <Button
          type='primary'
          onClick={() => this.generateFederalReport()}
          loading={locationIds.length === 0 || isLoading}
        >
          {isLoading ? 'Downloading...' : 'Download federal report'}
        </Button>
        <FormError message={errorMessage} />
      </div>
    )
  }
}
